import { z } from 'zod';
import { CaseSchema } from './case.js';
import { FlashcardSchema } from './flashcard.js';
import { McqSchema } from './mcq.js';
import { TrueFalseSchema } from './true-false.js';
import { EssaySchema } from './essay.js';
import { GlossaryItemSchema } from './glossary.js';
import { MindmapSchema } from './mindmap.js';
import { ProofDrillSchema } from './proof-drill.js';
import { LectureNoteSchema } from './lecture-note.js';
import { TopicNodeSchema } from './topic-node.js';
import { PastPaperSchema } from './past-paper.js';
import { MockExamItemsSchema } from './mock-exam-items.js';
import { FreeResponseSchema } from './free-response.js';
import { ReadingPassageSchema } from './reading-passage.js';
import { ExamFormatSchema } from './course-draft.js';
import { MasteryLevelSchema } from './mastery-level.js';
import { INGEST_PACKAGE_SCHEMA_VERSION } from './schema-version.js';
import { GapReportSchema } from './audit-gap-report.js';
import { ContentLanguageSchema } from './content-language.js';
/**
 * IngestPackageSchema — what one ingest run produces (LLM call OR manual paste).
 *
 * Lives in @examforge/shared because both producers (ingest worker) and consumers
 * (web frontend "Paste JSON" flow) need to agree on the shape. Single source of truth.
 *
 * Required: courseId / courseTitle / cases (≥1) / flashcards / mcqs.
 * Optional: trueFalse / essays / glossary / mindmaps — added when richer manual
 * generations include them. ResultView ignores optional fields it doesn't render yet.
 *
 * Why optional vs strict: during the "Claude in chat" workflow (TECH_DECISIONS adjustment
 * 2026-05-02), the human generator may include any subset; schema accepts anything
 * structurally valid, downstream UI shows what it knows how to render.
 */
// 19BH Day 2 Step 2.4 · explicit ZodObject annotation: each item schema is
// now z.preprocess(...) wrapping ItemTagsSchema.extend(...), which inflates
// the inferred type past tsc's serialization limit (TS7056). The annotation
// `: z.ZodObject<z.ZodRawShape>` lets the type system carry the shape without
// materializing the full inferred path.
//
// HOWEVER: this annotation erases all field types, causing 200+ TS4111/TS7006
// errors in consumers (web views, stats). Fix: keep the annotation for TS7056
// avoidance but override `IngestPackage` with an explicit type (see bottom).
export const IngestPackageSchema = z.object({
    /**
     * Session 19AL: schema version self-tag. New artefacts default to
     * `INGEST_PACKAGE_SCHEMA_VERSION`. Old artefacts without this field will
     * pass schema (optional) but flag below the minimum required version of any
     * view that calls `withMinSchema(...)`. See LLM_NATIVE_ARCHITECTURE §2.4 / §2.5.
     */
    schemaVersion: z.string().optional().default(INGEST_PACKAGE_SCHEMA_VERSION),
    courseId: z.string().regex(/^[a-z][a-z0-9_-]*$/, 'courseId must be lowercase slug'),
    courseTitle: z.string().min(1),
    // Required collections
    cases: z.array(CaseSchema).min(1),
    flashcards: z.array(FlashcardSchema),
    mcqs: z.array(McqSchema),
    // Optional collections (forward-compatible)
    trueFalse: z.array(TrueFalseSchema).optional(),
    essays: z.array(EssaySchema).optional(),
    glossary: z.array(GlossaryItemSchema).optional(),
    mindmaps: z.array(MindmapSchema).optional(),
    /** Session 19D: STEM derivation drill items. */
    proofDrills: z.array(ProofDrillSchema).optional(),
    /** Session 19Y: per-case lecture summary notes (ingest stage existed since 19S). */
    lectureNotes: z.array(LectureNoteSchema).optional(),
    /**
     * Session 19K (CONTENT_SPEC v0.1.0 §1.2): flat 3-level topic hierarchy.
     * Items reference nodes via caseId (chapter) / topicId / subtopicId.
     * Optional during transition: old data has none; new ingest produces it.
     */
    topicHierarchy: z.array(TopicNodeSchema).optional(),
    /**
     * Session 19S §C-1: Parsed past papers used as reference for mock generation.
     * Optional. Multiple allowed; MockPaperSpec.pastPaperRef points to one by id.
     */
    pastPapers: z.array(PastPaperSchema).optional(),
    /**
     * Session 19S §C-1: Per-paper independent mock items.
     * Optional. mock-exam-view samples from this when present, falls back to
     * pkg.mcqs / pkg.trueFalse with a UI banner when absent.
     */
    mockExamItems: MockExamItemsSchema.optional(),
    /**
     * Session 19AN: free-response items (short_answer / calculation / translation).
     * Unified container — single view, single ingest stage, single i18n
     * namespace. Free-tier self-assessment; aiScore reserved for premium.
     */
    freeResponses: z.array(FreeResponseSchema).optional(),
    /**
     * Session 19AN: English reading passages with linked mcq / trueFalse
     * questions. Generic — IELTS / TOEFL / 考研英语 specializations are future
     * templates. questionRefs cross-links resolve into pkg.mcqs / pkg.trueFalse.
     */
    readingPassages: z.array(ReadingPassageSchema).optional(),
    /**
     * Session 19AN · LLM_NATIVE §3 stale state: 8-char hash of the CourseDraft
     * inputs (sources / discipline / pedagogy / contentLanguage / mockPapers
     * config) at generation time. The frontend recomputes the current draft's
     * hash and compares — mismatch ⇒ artefact is stale relative to the draft,
     * UI surfaces a "数据已变，建议重新生成" banner. See packages/web/src/
     * lib/source-hash.ts for the canonical hashing function.
     */
    sourceHash: z.string().optional(),
    /**
     * Sprint 19BH Day 3 Step 3.6 · v4 audit framework output (no-LLM).
     *
     * Phase 4 audit step.do produces a GapReport per ingest run and finalize
     * embeds it here. Frontend (19BI · regen UI · Mock 中心) reads gapReport
     * to render badge counts, per-chapter gap drill-down, and `suggestedAction`
     * one-click regen buttons.
     *
     * Optional during the rollout: ingest runs from before 19BH won't have
     * one; migrateResultV3ToV4 (Day 5 Step 5.1) populates with defaultEmptyGapReport
     * when missing so consumers always see a uniform shape.
     */
    gapReport: GapReportSchema.optional(),
    /**
     * Session 19BI (DEBT-19BI-REGEN-DRAFT-INPUTS): Draft-derived fields persisted
     * by finalizePackage for regen workflow context. Previously survived only via
     * .passthrough(); now explicit so .strict() would not silently drop them.
     *
     * P0 fix 2026-07-03 (DEBT-19BO+-CONTENTLANGUAGE-SCHEMA-DRIFT): this was
     * `z.enum(['zh','en','ja','bilingual'])` — a hand-written enum that did NOT
     * match ContentLanguageSchema (en/en_zh_gloss/zh_en_terms/zh). Since
     * CourseDraft.contentLanguage defaults to 'en_zh_gloss' and the entire
     * generate chain threads that value through to finalizePackage, every
     * default-configured course failed IngestPackageSchema at finalize → package
     * discarded → 5/5 staging baseline runs wasted. 'ja'/'bilingual' had zero
     * consumers in the codebase. Unified to ContentLanguageSchema for parity
     * with CourseDraft + StagedIngestRequestSchema + all generate stages.
     */
    contentLanguage: ContentLanguageSchema.optional(),
    discipline: z.string().optional(),
    customDiscipline: z.string().optional(),
    /** 19BO+ P2-1: persisted so regen + route can read the original exam scenario. */
    examType: z.string().optional(),
    pedagogyTypes: z.array(z.string()).readonly().optional(),
    customPedagogy: z.string().optional(),
    examFormat: ExamFormatSchema.optional(),
    masteryLevel: MasteryLevelSchema.optional(),
}).passthrough();
//# sourceMappingURL=ingest-package.js.map